import type { SimulationState } from "./simulate";
import { nodeTitle, portLabel } from "./nodeDisplay";

interface SimulatorProps {
  simulation: SimulationState | null;
  onStart: () => void;
  onChoose: (outcome: string) => void;
  onStop: () => void;
}

/**
 * A structural "test call" panel: starts/stops a simulate.ts walkthrough
 * and lets a tester pick an outcome at each decision node. Owns no state of
 * its own — `simulation` is FlowPreview's, alongside `document`/`history`/
 * selection, so a document edit, undo/redo or example switch can invalidate
 * an in-progress test call the same explicit way those already clear
 * selection, rather than this component guessing from a changed prop.
 */
export function Simulator({ simulation, onStart, onChoose, onStop }: SimulatorProps) {
  return (
    <aside className="card simulator" aria-labelledby="simulator-heading">
      <h2 id="simulator-heading">Simulate a call</h2>
      {!simulation ? (
        <>
          <p className="card__note">
            Walks this document's own edges by port, pausing at gather/branch/repeat/amd_gate/
            transfer nodes so you pick the outcome — not a real execution proof (no TTS, HTTP, AMD
            or DTMF actually happen), just whether every path a caller could take goes somewhere.
          </p>
          <button type="button" onClick={onStart}>
            Start simulation
          </button>
        </>
      ) : (
        <>
          <ol className="simulator__trace">
            {simulation.trace.map((entry, index) => (
              <li key={index}>
                <code>{entry.nodeId}</code> <span className="simulator__type">({nodeTitle(entry.nodeType)})</span>
                {entry.arrivedVia ? (
                  <span className="simulator__via"> via &ldquo;{portLabel(entry.arrivedVia)}&rdquo;</span>
                ) : null}
              </li>
            ))}
          </ol>
          {simulation.ended ? (
            <p
              className={
                simulation.endReason === "hangup" || simulation.endReason === "transfer succeeded"
                  ? "card__note"
                  : "card__error"
              }
            >
              Ended: {simulation.endReason}
            </p>
          ) : (
            <div className="simulator__choices">
              <p className="card__note">
                Choose an outcome for <code>{simulation.currentNodeId}</code>:
              </p>
              {simulation.choices.map((choice) => (
                <button key={choice} type="button" className="btn--outline" onClick={() => onChoose(choice)}>
                  {portLabel(choice)}
                </button>
              ))}
            </div>
          )}
          <button type="button" className="inspector__delete" onClick={onStop}>
            Stop
          </button>
        </>
      )}
    </aside>
  );
}
